import * as React from "react";
import { decryptTheParams } from "../../../../Helper/QueryParams/EncryptDecrypt";
import { useBatteryInfo, } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import BatteryAlertLogs from "./BatteryAlertLogs";
import { DigitalTwinTabContainer } from "../../../../Theme/DigitalTwinStyles";
import BatteryBmsFaults from "./BatteryBmsFaults";
import BatteryPlatformAlertsV2 from "./BatteryPlatformAlertsV2";
import BatteryAlertsTabs from "./BatteryAlertsTabs";
import { Box } from "@mui/material";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import CartLoader from "../../../../Helper/Loaders/CartLoader";
import { usePermissions } from "../../../../Context/PermissionContext";
import { FEATURE_CODES } from "../../../../Static/FeatureMapping";
import { getAvailableAlertTabs } from "./alertTabsConfig";

export default function BatteryAlerts() {
    const { batteryID } = useBatteryInfo()
    const pageContent = usePagesContent();
    const { hasPermission, isRbacV2Enabled } = usePermissions();
    // Show Platform Alerts V2 only when RBAC v2 is enabled AND the permission is granted.
    // hasPermission() returns true by default when RBAC v2 is disabled (V1 fallback), so we
    // also gate on isRbacV2Enabled() — otherwise RBAC-v2-off orgs would get V2 instead of the
    // legacy Alert and Warning Log.
    const hasPlatformAlertsV2 = isRbacV2Enabled() && hasPermission(FEATURE_CODES.PLATFORM_ALERTS_V2, 'view');
    // Tab visibility: when RBAC v2 is off, hasPermission() returns true (V1 fallback) so legacy
    // orgs keep seeing the tab with the legacy Alert and Warning Log. When RBAC v2 is on, the tab
    // is hidden unless the user has the PLATFORM_ALERTS_V2 view permission.
    const canViewPlatformAlerts = hasPermission(FEATURE_CODES.PLATFORM_ALERTS_V2, 'view');
    // Same idiom as platform alerts: hasPermission() returns true when RBAC v2 is
    // off (V1 fallback) so legacy orgs keep the tab; when RBAC v2 is on, the BMS
    // Faults tab is hidden unless the user has the BMS_FAULTS_V2 view permission.
    const canViewBmsFaults = hasPermission(FEATURE_CODES.BMS_FAULTS_V2, 'view');
    const queryParams = decryptTheParams();

    const enabledFeatures = pageContent?.metaInfo?.enabledFeatures || [];
    const availableTabs = getAvailableAlertTabs(enabledFeatures, { canViewPlatformAlerts, canViewBmsFaults });

    const getInitialTab = () => {
        const requested = queryParams?.alertsSubTab;
        if (requested && availableTabs.some(t => t.slug === requested)) return requested;
        return availableTabs[0]?.slug ?? null;
    };

    const [selectedTab, setSelectedTab] = React.useState(getInitialTab);

    if (!pageContent) {
        return (
            <DigitalTwinTabContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CartLoader />
                </Box>
            </DigitalTwinTabContainer>
        );
    }

    return (
        <DigitalTwinTabContainer>
            {availableTabs.length > 1 && (
                <BatteryAlertsTabs
                    tabs={availableTabs}
                    selectedTab={selectedTab}
                    onChange={setSelectedTab}
                />
            )}
            <Box sx={{mb: 6}}>
                {selectedTab === 'bms-faults' && <BatteryBmsFaults batteryID={batteryID} />}
                {selectedTab === 'platform-alerts' && (
                    hasPlatformAlertsV2
                        ? <BatteryPlatformAlertsV2 batteryID={batteryID} />
                        : <BatteryAlertLogs batteryID={batteryID} />
                )}
            </Box>
        </DigitalTwinTabContainer>
    )
}
